import {
  ChangeDetectionStrategy,
  Component,
  ViewContainerRef,
} from "@angular/core";
import * as composeActions from "@bca-compose/store/compose.actions";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import { RouterService } from "@bca-core/services/router.service";
import { UIService } from "@bca-core/services/ui.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { ConfirmDialog } from "@bca-shared/components/confirm-dialog/confirm-dialog";
import {
  DialogIdentifier,
  ProductIdentifier,
  Route,
} from "@bca-shared/models/_enums";
import { DialogId } from "@bca-shared/models/_types";
import { IProduct } from "@bca-shared/models/product";
import { AvoEventTypes } from "@bca-shared/utils/avo/avo-event-details.models";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectAllProducts,
  selectIncompletedProducts,
  selectTotalPrice,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, map, take, withLatestFrom } from "rxjs/operators";

@Component({
  selector: "bca-receipt",
  templateUrl: "receipt.component.html",
  styleUrls: ["receipt.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiptComponent {
  products$ = this._store$.pipe(select(selectAllProducts));
  totalProductCount$ = this._store$.pipe(select(selectTotalProductCount));
  totalReceiptPrice$ = this._store$.pipe(select(selectTotalPrice));
  selectedProduct$ = this._store$.pipe(
    select(({ compose }) => compose.selectedProduct),
  );
  currentRoute$ = this._store$.pipe(select(({ app }) => app.currentRoute));
  routeLoading$ = this._store$.pipe(
    select(({ compose }) => compose.routeLoading),
    combineLatest(this._store$.pipe(select(({ app }) => app.dialogStatus))),
    map(
      ([routeLoading, dialogStatus]) =>
        routeLoading && !dialogStatus[ProductIdentifier.Traject],
    ),
  );
  isComposingProduct$ = this.totalProductCount$.pipe(map((count) => count > 0));

  Route = Route;

  constructor(
    private _store$: Store<IRootState>,
    private _uiService: UIService,
    private _routerService: RouterService,
    private _translate: TranslateService,
    private _dialogService: DialogService,
    private _viewContainerRef: ViewContainerRef,
    private _avoTrackingService: AvoTrackingService,
  ) {}

  onProductDelete(product: IProduct): void {
    const confirmDialog = this._dialogService.open(
      ConfirmDialog,
      DialogIdentifier.Confirm,
      this._viewContainerRef,
      {
        disableClose: true,
      },
      {
        title: this._translate.instant("DIALOG.PRODUCT.CONFIRM_DELETE_TITLE"),
        body: this._translate.instant("DIALOG.PRODUCT.CONFIRM_DELETE_BODY", {
          subject: this._translate.instant(
            `COMPOSE.PRODUCTS_OVERVIEW.PRODUCTS.${product.identifier}.TITLE`,
          ),
        }),
        origin: "removeFromCart",
        product: product,
      },
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    confirmDialog.instance
      .accept(false)
      .pipe(
        withLatestFrom(
          this._store$.pipe(select(({ compose }) => compose.selectedProduct)),
          this._store$.pipe(select(({ userData }) => userData.selectedProduct)),
          this._store$.pipe(
            select(selectIncompletedProducts),
            map((products) =>
              products.filter((prod) => prod.id !== product.id),
            ),
          ),
          this._store$.pipe(
            select(selectAllProducts),
            map((products) =>
              products.filter((prod) => prod.id !== product.id),
            ),
          ),
          this._store$.pipe(select(selectTotalProductCount)),
        ),
      )
      .subscribe(
        ([
          ,
          productCompose,
          productUserData,
          incompletedProducts,
          products,
          totalProductCount,
        ]) => {
          if (productUserData && productUserData.id === product.id) {
            if (incompletedProducts.length) {
              const nextProduct = [...incompletedProducts].shift();
              this._store$.dispatch(
                this._createAction(
                  new userDataActions.SelectProduct(
                    nextProduct ? nextProduct : null,
                  ),
                ),
              );
            } else {
              const nextProduct = [...products].shift();
              this._store$.dispatch(
                this._createAction(
                  new userDataActions.SelectProduct(
                    nextProduct ? nextProduct : null,
                  ),
                ),
              );
            }
          }

          if (productCompose && productCompose.id === product.id) {
            this._store$.dispatch(
              this._createAction(new composeActions.SelectProduct(null)),
            );
            this._store$.dispatch(
              this._createAction(new composeActions.SetConfiguredProduct(null)),
            );
          } else {
            this._store$.dispatch(
              this._createAction(new receiptActions.DeleteProduct(product)),
            );
            this._uiService.getUserStorage();
          }

          const productCount = totalProductCount - 1;

          if (productCount > 0) {
            confirmDialog.close();
          } else {
            this._dialogService.closeAll();
          }
        },
      );
  }

  async onProductEdit(product: IProduct): Promise<void> {
    this.handleAvoEvents("order_change", product);
    this._uiService.editProductClicked$.next(product);
    this._dialogService.close(DialogIdentifier.Receipt);

    this.selectedProduct$
      .pipe(
        take(1),
        withLatestFrom(
          this._store$.pipe(select(({ userData }) => userData.selectedProduct)),
        ),
      )
      .subscribe(async ([productCompose, productUserData]) => {
        const mergedProduct = {
          ...product,
          user: {
            ...product.user,
            ...(productUserData &&
            productUserData.user &&
            productUserData.id === product.id
              ? productUserData.user
              : null),
          },
        };

        this._store$.dispatch(
          this._createAction(new userDataActions.SelectProduct(mergedProduct)),
        );

        if (mergedProduct.identifier === ProductIdentifier.Traject) {
          this._store$.dispatch(
            this._createAction(
              new composeActions.SetConfiguredProduct(mergedProduct),
            ),
          );
        } else {
          this._store$.dispatch(
            this._createAction(new composeActions.SetConfiguredProduct(null)),
          );
        }

        const navigatedAway = await this._routerService.navigateTo(
          Route.COMPOSE,
        );
        if (!navigatedAway) {
          if (productCompose) {
            this._store$.dispatch(
              this._createAction(new receiptActions.AddProduct(productCompose)),
            );
          }
          this._store$.dispatch(
            this._createAction(
              new composeActions.SelectProduct({
                product: mergedProduct,
                scrollToExtras: false,
              }),
            ),
          );
        }
      });
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, ReceiptComponent);
  }

  private handleAvoEvents(eventType: AvoEventTypes, product: IProduct): void {
    this._avoTrackingService.trackOrderChange(product);
  }
}
